'use client';

import { useState } from 'react';
import { useBuyNeuro } from '@hooks/credits/useBuyNeuro';
import { StatusPayment } from '@components/features/payment';
import { DiscountCalculator } from './partials/DiscountCalculator';
import { PaymentMethods } from './partials/PaymentMethods';

/**
 * @description Hub interface.
 */
interface BuyNeuroHubProps {
  closeModal: () => void,
  targetAmount?: number,
}

/**
 * @description Common transition props.
 */
const transitionProps = {
  show: true,
  appear: true,
  unmount: false,
  enter: 'transition-opacity duration-300',
  enterFrom: 'opacity-0',
  enterTo: 'opacity-100',
};

/**
 * @description Renders the Buy Neuro hub for modal.
 * - `closeModal` - Function to close the modal.
 */
export const BuyNeuroHub = (props: BuyNeuroHubProps) => {
  // Destructure props
  const {
    closeModal,
    targetAmount,
  } = props;

  // State for the current step. Use the component names as steps to avoid mismatches.
  const [currentStep, setCurrentStep] = useState<string>('DiscountCalculator');

  // Buy neuro hook
  const {
    createShoppingCart,
    loadingCartId,
    shoppingCartId,
    loadingCartDetails,
    cartDetails,
    updateShoppingCart,
    loadingUpdatedCartDetails,
    cartDetailsUpdated,
    orderCredits,
    deleteShoppingCart,
    savingOrder,
    orderError,
    paths,
  } = useBuyNeuro({
    stepSetter: setCurrentStep,
  });

  // Render component
  // ********************************
  return (
    <>
      {/* Step 1- Discount calculator */
        currentStep === 'DiscountCalculator' && (
          <DiscountCalculator
            transitionProps={transitionProps}
            triggerCartDetails={shoppingCartId?.id ? updateShoppingCart : createShoppingCart}
            loadingNextStep={!!(loadingCartId && loadingCartDetails) || loadingUpdatedCartDetails}
            targetAmount={targetAmount}
            prevCreditsAmount={cartDetailsUpdated?.order?.amount ?? cartDetails?.order?.amount}
            closeModal={closeModal}
          />
        )
      }

      {/* Step 2 - Payment methods */
        currentStep === 'PaymentMethods' && (
          <PaymentMethods
            transitionProps={transitionProps}
            cartDetails={cartDetailsUpdated ?? cartDetails}
            orderCredits={orderCredits}
            savingOrder={savingOrder}
            orderError={orderError}
            stepBack={() => {
              setCurrentStep('DiscountCalculator');
            }}
            deleteShoppingCart={() => {
              deleteShoppingCart();
              closeModal();
            }}
          />
        )
      }

      {/* Step 3a - Completed & paid with credit card */
        currentStep === 'CompletedCard' && (
          <StatusPayment
            className="bg-surface-100"
            orderStatus="success"
            actionSuccess={closeModal}
          />
        )
      }

      {/* Step 3b - Completed but not paid (invoice) */
        currentStep === 'CompletedInvoice' && (
          <StatusPayment
            className="bg-surface-100"
            orderStatus="success"
            customTitle="order.success.title"
            customMessage="order.checkout.payment.invoice"
            customButtonLabel="page.after.activation.job.button.invoices"
            withAction={paths?.toInvoice !== undefined}
            actionSuccess={paths?.toInvoice}
          />
        )
      }

      {/* Step 3c - Not completed */
        currentStep === 'NotCompleted' && (
          <StatusPayment
            className="bg-surface-100"
            orderStatus="error"
            customTitle="order.failed.title"
            customMessage="order.failed.message"
            customButtonLabel="global.label.see_details"
            withAction={paths?.notCompleted !== undefined}
            actionError={paths?.notCompleted}
          />
        )
      }

      {/* Step 3d - Error */
        currentStep === 'Error' && (
          <StatusPayment
            className="bg-surface-100"
            orderStatus="error"
            actionError={() => setCurrentStep('PaymentMethods')}
          />
        )
      }
    </>
  );
};
